/**
 * Contract for the admin regional product catalogue.
 *
 * This module is deliberately free of runtime imports (type-only imports are
 * erased) so frontend packages can import the region table directly without
 * pulling an AWS SDK or other infrastructure chain across the
 * frontend/backend boundary.
 */
import type { Locale } from "./localization";
import type { Product } from "./product";

/** Business units a catalogue region can be scoped to. */
export type CatalogueBusinessUnit = "BUS" | "BAU" | "BUK" | "BDE";

export const CATALOGUE_REGION_IDS = ["US", "AU", "UK", "DE", "NL"] as const;

export type CatalogueRegionId = (typeof CATALOGUE_REGION_IDS)[number];

/** Sales channels that tell the regions sharing a business unit apart. */
export type CatalogueSalesChannel = "BDEMP" | "BNLMP";

/**
 * A catalogue region is a storefront market, not a coffee origin. DE and NL
 * share the BDE business unit and are told apart by their sales channel, and
 * the locale is explicit so NL titles are never rendered in German.
 */
export interface CatalogueRegion {
  id: CatalogueRegionId;
  businessUnit: CatalogueBusinessUnit;
  channel?: CatalogueSalesChannel;
  locale: Locale;
}

/** Single source of truth for the allowed catalogue regions. */
export const CATALOGUE_REGIONS: Record<CatalogueRegionId, CatalogueRegion> = {
  US: { id: "US", businessUnit: "BUS", locale: "en_US" },
  AU: { id: "AU", businessUnit: "BAU", locale: "en_AU" },
  UK: { id: "UK", businessUnit: "BUK", locale: "en_GB" },
  DE: { id: "DE", businessUnit: "BDE", channel: "BDEMP", locale: "de_DE" },
  NL: { id: "NL", businessUnit: "BDE", channel: "BNLMP", locale: "nl_NL" },
};

/**
 * The sales channel a region's storefront status lives on: the region's own channel (DE/NL), otherwise the business
 * unit's marketplace channel. Admin status changes always target this channel.
 */
export const catalogueStorefrontChannel = (region: CatalogueRegion): string => region.channel ?? `${region.businessUnit}MP`;

export const isCatalogueRegionId = (value: unknown): value is CatalogueRegionId =>
  typeof value === "string" && (CATALOGUE_REGION_IDS as readonly string[]).includes(value);

export interface AdminCatalogueContext {
  region: CatalogueRegionId;
  businessUnit: CatalogueBusinessUnit;
  channel?: CatalogueSalesChannel;
  locale: Locale;
  /** Number of parent products in `products`. */
  productCount: number;
  /** Number of SKUs (variants) across all products. */
  skuCount: number;
  /** Number of upstream PIM pages that were walked to build the catalogue. */
  pages: number;
  /** ISO timestamp of when the catalogue was fetched. */
  fetchedAt: string;
}

export interface AdminCatalogueResponse {
  products: Product[];
  context: AdminCatalogueContext;
}
